/**
 * Dashboard card helpers: the order studies appear in, and the uploaded
 * protocol file name shown under a card's title.
 */

/** Newest first by creation time. Studies with no date go last. Ties keep their
 * original order. Returns a new array. */
export function sortProjectsNewestFirst<T extends { createdAt?: number | null }>(
  projects: readonly T[],
): T[] {
  const has = (p: T): p is T & { createdAt: number } =>
    typeof p.createdAt === 'number' && Number.isFinite(p.createdAt);
  return projects
    .map((p, i) => ({ p, i }))
    .sort((a, b) => {
      const ad = has(a.p);
      const bd = has(b.p);
      if (ad && bd) {
        const diff = (b.p as { createdAt: number }).createdAt - (a.p as { createdAt: number }).createdAt;
        return diff !== 0 ? diff : a.i - b.i;
      }
      if (ad !== bd) return ad ? -1 : 1;
      return a.i - b.i;
    })
    .map(x => x.p);
}

/**
 * The file name Jim uploaded, from a stored path such as
 * `projects/<id>/protocols/1791737219809_Fluciclovine_and_PSMA.pdf`.
 * Upload prefixes `Date.now()_` (13 digits) and turns every character other
 * than letters, digits, dot, dash and underscore into an underscore, so a
 * space and a real underscore cannot be told apart: the name is shown as
 * stored. Returns null when there is no file.
 */
export function protocolFileDisplayName(path: string | null | undefined): string | null {
  if (typeof path !== 'string') return null;
  const last = path.split(/[/\\]/).pop() ?? '';
  const name = last.replace(/^\d{13}_/, '');
  return name.length > 0 ? name : null;
}
